import { useEffect, useState } from 'react';
import { apiUnbatched, call } from './api.js';
import type { NavCounts } from '../../../api/src/catalog/nav-counts.js';

/**
 * THE NAV COUNTS, FETCHED ONCE AND SHARED (area-chip redesign 10b). Two consumers need the same
 * numbers: `AppNav` (the sheet's per-row subtitles + warn badges) and `AreaChip` (the header
 * band's waiting number). A module store hoists the fetch to its first use so the read runs ONCE
 * for both, rather than once per screen — the DRY rule's "hoist a fact at the first duplicate".
 *
 * ⚠️ NOT POLLED, BUT RE-READ WHENEVER THE SHEET OPENS (0.1.395). It used to be fetched exactly
 * once per JS context and never invalidated, on the judgement that "the nav is a hint on the way
 * past". That judgement was wrong in the one place the number is actually read: closing or
 * deleting a box updated the Boxes tab while this cache kept the pre-mutation number for the rest
 * of the session, so the sheet said "1 open" over a tab showing two. Reopening the app does not
 * clear it either — an installed PWA resumed from the app switcher keeps the same JS context, and
 * that is exactly how the maintainer saw a wrong count survive a "restart" (field report,
 * 26 Aug 2026). The number is now re-read at the moment it is shown, which is the only moment it
 * has to be true. A failed read leaves the PREVIOUS value standing (`call` has ledgered it and the
 * target screen reports the same failure loudly) — a degrade, not a silence.
 */
let counts: NavCounts | null = null;
let inflight = false;
const listeners = new Set<(c: NavCounts | null) => void>();

async function read(): Promise<void> {
  if (inflight) return;
  inflight = true;
  // Its own request, never the boot batch's (0.1.481, see `apiUnbatched`): the landing screen
  // paints on its own reads; these numbers arrive when they arrive.
  const got = await call('navCounts', () => apiUnbatched.navCounts.query());
  inflight = false;
  if (got.ok) {
    counts = got.data;
    for (const listener of listeners) listener(counts);
  }
}

async function load(): Promise<void> {
  if (counts !== null) return;
  await read();
}

/**
 * RE-READ NOW — called when the nav sheet opens, so the subtitles are the truth at the moment
 * they are drawn rather than the truth at first paint. Concurrent calls collapse onto the one
 * in-flight read (`read` guards that), so opening the sheet twice quickly is one request.
 */
export async function refreshNavCounts(): Promise<void> {
  await read();
}

/** The counts as last read, for a caller that must compare rather than subscribe (the Boxes
 *  screen's disagreement diagnostic). Never triggers a read of its own. */
export function navCountsSnapshot(): NavCounts | null {
  return counts;
}

export function useNavCounts(): NavCounts | null {
  const [current, setCurrent] = useState<NavCounts | null>(counts);
  useEffect(() => {
    listeners.add(setCurrent);
    void load();
    return () => {
      listeners.delete(setCurrent);
    };
  }, []);
  return current;
}
